import { useEffect, useState } from 'react';
import { Box, Typography } from '@material-ui/core';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { InheritedCatalogContext } from './InheritedCatalogContext';
import { intakeContext } from './intakeContext';

export function HorizonScopeSummary(
  props: FieldExtensionComponentProps<string>,
) {
  const discovery = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const scope = intakeContext(props).workloadRef;
  const [summary, setSummary] = useState<{
    contentSha256: string;
    children: {
      resourceRef: string;
      instanceId: string;
      accountId: string;
      region: string;
      networkId: string;
    }[];
  } | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let active = true;
    setSummary(null);
    setError('');
    if (typeof scope !== 'string' || !scope.startsWith('asscope-'))
      return () => {
        active = false;
      };
    void discovery
      .getBaseUrl('horizon-assess')
      .then(base =>
        fetchApi.fetch(`${base}/v1/cloud/scopes/${encodeURIComponent(scope)}`),
      )
      .then(async response => {
        if (!response.ok)
          throw new Error(
            'Selected scope could not be read. Return to source scope and refresh.',
          );
        const value = await response.json();
        if (
          !value ||
          !Array.isArray(value.children) ||
          typeof value.contentSha256 !== 'string'
        )
          throw new Error('Invalid scope preview response');
        if (active) setSummary(value);
      })
      .catch(caught => {
        if (active)
          setError(
            caught instanceof Error
              ? caught.message
              : 'Scope preview unavailable',
          );
      });
    return () => {
      active = false;
    };
  }, [scope, discovery, fetchApi]);
  return (
    <Box>
      <Typography variant="h6">Selected source scope</Typography>
      <Typography>
        {typeof scope === 'string' ? scope : 'No source selected'}
      </Typography>
      {error && <Typography color="error">{error}</Typography>}
      {summary && (
        <>
          <Typography>
            {summary.children.length} explicitly selected assessment roots
          </Typography>
          <details open>
            <summary>Exact instance identities</summary>
            {summary.children.map(item => (
              <Typography
                key={`${item.accountId}/${item.region}/${item.instanceId}`}
              >
                {item.accountId} / {item.region} / {item.networkId} /{' '}
                {item.instanceId}
              </Typography>
            ))}
          </details>
          <Typography variant="caption">
            Accepted scope SHA-256: {summary.contentSha256}
          </Typography>
        </>
      )}
      <InheritedCatalogContext
        resourceRefs={
          summary?.children.map(item => item.resourceRef) ??
          (typeof scope === 'string' && scope.startsWith('resource:')
            ? [scope]
            : [])
        }
      />
      <Typography variant="body2">
        Scope is revalidated before collection. Changing the selected servers
        requires a new scope. Discovered dependencies are references, not
        automatic guest or migration targets.
      </Typography>
    </Box>
  );
}
